// S2-CC20.6b.5: ReconciliationView — composition that wraps
// useLLMReconciliation() into the 3-tile grid (ledger / provider / drift)
// + the status banner below.
//
// Today the backend returns status='not_configured' always (CC5a stub), so
// provider_total_cents and drift_cents are always null. The UI is written
// to handle all three future states as well:
//   - 'not_configured' → stub tiles + copper banner
//   - configured + drift > 100¢ → drift-warn color + error banner
//   - configured + drift ≤ 100¢ → colored value, no banner
//
// Wireframe: docs/sprint-2/wireframes/s2-cc6-llm-control-center.html
// (Section 9 — Reconciliation).
import { Card } from '../Card';
import { useLLMReconciliation } from '../../hooks/useLLMReconciliation';
import './ReconciliationView.css';

// Format cents: integer → "19¢", fractional → "19.1¢" (one decimal).
// Kept local (not in a shared util) because it's a three-line helper and
// no other place in the dashboard needs it today — extract when a 2nd
// call site appears.
export function formatCents(cents: number): string {
  if (Number.isInteger(cents)) return `${cents}¢`;
  return `${cents.toFixed(1)}¢`;
}

// Format the "as of" HH:MM:SS caption from ISO timestamp.
function formatAsOf(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

// Drift magnitude → color class. Matches the wireframe:
//   |drift| < 1¢   → green  (aligned)
//   |drift| > 100¢ → amber  (investigate)
//   otherwise      → default (mono primary)
function driftColorClass(drift: number | null): string {
  if (drift === null) return 'airgent-llm-recon-value-stub';
  const mag = Math.abs(drift);
  if (mag < 1) return 'airgent-llm-recon-value-ok';
  if (mag > 100) return 'airgent-llm-recon-value-warn';
  return '';
}

export function ReconciliationView() {
  const { snapshot, loading, error, refresh } = useLLMReconciliation();

  // Loading state: snapshot is null before the first fetch completes.
  if (loading && !snapshot && !error) {
    return (
      <Card variant="standard" padding="default" className="airgent-llm-reconciliation-view">
        <div className="airgent-llm-card-header">
          <div>
            <div className="airgent-llm-card-title">Reconciliation</div>
            <div className="airgent-llm-card-sub">
              Our ledger vs provider-reported spend · drift is the bypass-leak signal
            </div>
          </div>
        </div>
        <div className="airgent-llm-loading-caption">Loading reconciliation…</div>
      </Card>
    );
  }

  // Error state — tiles would be misleading with no data, so we render a
  // banner + retry in place of the grid.
  if (error && !snapshot) {
    return (
      <Card variant="standard" padding="default" className="airgent-llm-reconciliation-view">
        <div className="airgent-llm-card-header">
          <div>
            <div className="airgent-llm-card-title">Reconciliation</div>
            <div className="airgent-llm-card-sub">
              Our ledger vs provider-reported spend · drift is the bypass-leak signal
            </div>
          </div>
        </div>
        <div className="airgent-llm-error-banner" role="alert">
          <span>Failed to load reconciliation: {error}</span>
          <button
            type="button"
            className="airgent-llm-retry-btn"
            onClick={refresh}
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  // Past this point snapshot is guaranteed non-null — we only hit the
  // final return when the initial fetch succeeded OR when we already had
  // a snapshot from a previous successful load.
  if (!snapshot) return null;

  const providerNull = snapshot.provider_total_cents === null;
  const driftNull = snapshot.drift_cents === null;
  const driftClass = driftColorClass(snapshot.drift_cents);
  const driftMag = snapshot.drift_cents !== null ? Math.abs(snapshot.drift_cents) : 0;

  // Banner logic:
  //   - status='not_configured' → copper stub banner
  //   - otherwise + drift > 100 → red warn banner
  //   - otherwise → no banner
  const showStubBanner = snapshot.status === 'not_configured';
  const showDriftBanner =
    !showStubBanner && snapshot.drift_cents !== null && driftMag > 100;

  return (
    <Card variant="standard" padding="default" className="airgent-llm-reconciliation-view">
      <div className="airgent-llm-card-header">
        <div>
          <div className="airgent-llm-card-title">Reconciliation</div>
          <div className="airgent-llm-card-sub">
            Our ledger vs provider-reported spend · drift is the bypass-leak signal
          </div>
        </div>
        <div className="airgent-llm-card-sub">
          as of {formatAsOf(snapshot.as_of)} · cache 5s
        </div>
      </div>

      <div className="airgent-llm-recon-grid">
        {/* Tile 1 — our ledger total (always available). */}
        <div className="airgent-llm-recon-tile">
          <div className="airgent-llm-recon-label">Ledger total (ours)</div>
          <div className="airgent-llm-recon-value">
            {formatCents(snapshot.ledger_total_cents)}
          </div>
          <div className="airgent-llm-recon-foot">
            <span>from llm_calls</span>
            <span>today</span>
          </div>
        </div>

        {/* Tile 2 — provider-reported total. null until CC5a. */}
        <div className="airgent-llm-recon-tile">
          <div className="airgent-llm-recon-label">Provider total</div>
          <div
            className={`airgent-llm-recon-value${providerNull ? ' airgent-llm-recon-value-stub' : ''}`}
          >
            {providerNull
              ? 'Not configured'
              : formatCents(snapshot.provider_total_cents as unknown as number)}
          </div>
          <div className="airgent-llm-recon-foot">
            <span>Anthropic Admin API</span>
            <span>S2-CC5a</span>
          </div>
        </div>

        {/* Tile 3 — drift (signed). null until CC5a. */}
        <div className="airgent-llm-recon-tile">
          <div className="airgent-llm-recon-label">Drift</div>
          <div className={`airgent-llm-recon-value ${driftClass}`}>
            {driftNull
              ? '—'
              : `${(snapshot.drift_cents as unknown as number) >= 0 ? '+' : ''}${snapshot.drift_cents}¢`}
          </div>
          <div className="airgent-llm-recon-foot">
            <span>|provider − ledger|</span>
            <span>alert &gt; $1</span>
          </div>
        </div>
      </div>

      {/* Banner — below the tiles. Copper stub vs red drift-warn is chosen
          by the status flag; no banner when all three values are fine. */}
      {showStubBanner && (
        <div className="airgent-llm-recon-banner airgent-llm-recon-banner-stub" role="status">
          <span aria-hidden="true">⚠</span>
          <span>Provider-side fetch not wired. Awaiting S2-CC5a.</span>
        </div>
      )}
      {showDriftBanner && (
        <div className="airgent-llm-recon-banner airgent-llm-recon-banner-warn" role="alert">
          <span aria-hidden="true">▲</span>
          <span>
            Drift {snapshot.drift_cents}¢ — investigate potential SDK bypass or
            missed ledger write.
          </span>
        </div>
      )}
    </Card>
  );
}
